import { compose, prop, pathOr, path, assoc, includes, not, equals, isEmpty } from 'ramda';
import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { connect } from 'react-redux';
import { withTranslate } from '@rushplay/i18n';

// Types
var UPDATE_UNREAD_COUNT = 'support-chat/UPDATE_UNREAD_COUNT';
var SET_TAB_VISIBILTY = 'support-chat/SET_TAB_VISIBILTY';

// Actions
function updateUnreadCount(payload) {
  return {
    type: UPDATE_UNREAD_COUNT,
    payload: payload
  };
}

function setTabVisibility(payload) {
  return {
    type: SET_TAB_VISIBILTY,
    payload: payload
  };
}

// Selectors
function getUnreadCount(state) {
  return pathOr(0, ['unreadMessages'], state);
}

function getTabVisibility(state) {
  return path(['tabVisible'], state);
}

// Reducer
var initialState = {
  unreadMessages: 0,
  tabVisible: false
};

function reducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : initialState;
  var action = arguments[1];

  switch (action.type) {
    case UPDATE_UNREAD_COUNT:
      {
        // Only update if the chat is closed
        if (!getTabVisibility(state)) {
          return assoc('unreadMessages', action.payload, state);
        } else {
          return assoc('unreadMessages', 0, state);
        }
      }

    case SET_TAB_VISIBILTY:
      {
        return assoc('tabVisible', action.payload, state);
      }

    default:
      return state;
  }
}

function usePrev(value) {
  var ref = React.useRef(value);
  React.useEffect(function () {
    if (value !== ref.current) {
      ref.current = value;
    }
  }, [value]);
  return ref.current;
}

var _slicedToArray = function () { function sliceIterator(arr, i) { var _arr = []; var _n = true; var _d = false; var _e = undefined; try { for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"]) _i["return"](); } finally { if (_d) throw _e; } } return _arr; } return function (arr, i) { if (Array.isArray(arr)) { return arr; } else if (Symbol.iterator in Object(arr)) { return sliceIterator(arr, i); } else { throw new TypeError("Invalid attempt to destructure non-iterable instance"); } }; }();

/**
 * Department object, response of [webWidget:get chat:department]{@link https://developer.zendesk.com/embeddables/docs/widget/chat#get-chatdepartment}
 * @typedef {Object} DepartmentObject
 * @param {number} id
 * @param {number} name
 * @param {string} status
 */

/**
 * Helper function for checking status of primary & fallback department
 * @param {DepartmentObject} primary
 * @param {DepartmentObject} fallback
 */
function handleDepartmentStatus() {
  var primary = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var fallback = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

  if (primary.status !== 'online') {
    if (fallback.status === 'online') {
      /**
       * Sets the new active department to the fallback in the global zESettings object
       * [webWidget updateSettings]{@link https://developer.zendesk.com/embeddables/docs/widget/core#updatesettings}
       */
      zE(function () {
        zE('webWidget', 'updateSettings', {
          webWidget: { chat: { departments: { select: fallback.id } } }
        });
        /**
         * zE doesn't have a way of setting status of webwidget yet.
         * According to the migration-guide the old way is however still supported.
         * [Migration Guide - setStatus]{@link https://developer.zendesk.com/embeddables/docs/widget/chat_api_migration#customization-apis}
         * [$zopim.livechat.setStatus]{@link https://api.zopim.com/files/meshim/widget/controllers/LiveChatAPI-js.html}
         */
        $zopim(function () {
          $zopim.livechat.setStatus('online');
        });
      });
    } else {
      // neither primary nor fallback was online
      zE(function () {
        $zopim(function () {
          $zopim.livechat.setStatus('offline');
        });
      });
    }
  } else {
    // primary is online
    zE(function () {
      zE('webWidget', 'updateSettings', {
        webWidget: { chat: { departments: { select: primary.id } } }
      });
      $zopim(function () {
        $zopim.livechat.setStatus('online');
      });
    });
  }
}

function Chat(props) {
  var _React$useState = React.useState(false),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      visible = _React$useState2[0],
      setVisible = _React$useState2[1];

  /**
   * This hook manages listener for media-query that updates local-state when matching
   * if the widget is not hidden.
   */


  React.useEffect(function () {
    function handleVisibility(e) {
      setVisible(e.matches);
    }
    if (!props.hidden) {
      var mq = window.matchMedia('(min-width: ' + props.visibleAt + 'px)');

      setVisible(mq.matches);

      mq.addListener(handleVisibility);

      return function () {
        mq.removeListener(handleVisibility);
      };
    }
  }, [props.visibleAt, props.hidden]);

  /**
   * This hook handles funneling user into the correct departments
   */
  React.useEffect(function () {
    if (props.departmentId || props.fallbackDepartmentId) {
      zE(function () {
        /**
         * Registers a callback to be fired when the widget successfully connects to the server.
         * [webWidget:on chat:connected]{@link https://developer.zendesk.com/embeddables/docs/widget/chat#on-chatconnected}
         */
        zE('webWidget:on', 'chat:connected', function () {
          /**
           * Gets the data of a specific department
           * [webWidget:get chat:department]{@link https://developer.zendesk.com/embeddables/docs/widget/chat#get-chatdepartment}
           * @returns DepartmentObject
           */
          var primaryDepartment = zE('webWidget:get', 'chat:department', props.departmentId);
          var fallbackDepartment = zE('webWidget:get', 'chat:department', props.fallbackDepartmentId);

          handleDepartmentStatus(primaryDepartment, fallbackDepartment);

          /**
           * Registers a callback to be fired when a department status changes.
           * [webWidget:on chat:departmentStatus]{@link https://developer.zendesk.com/embeddables/docs/widget/chat#on-chatdepartmentstatus}
           */
          zE('webWidget:on', 'chat:departmentStatus', function (dep) {
            /**
             * This callback is fired on all department changes.
             * We do however only care about departmentId & fallbackDepartmentId
             */
            if (includes(dep.id, [props.departmentId, props.fallbackDepartmentId])) {
              // Re-fetching {DepartmentObject} to have updated statuses
              var _primaryDepartment = zE('webWidget:get', 'chat:department', props.departmentId);
              var _fallbackDepartment = zE('webWidget:get', 'chat:department', props.fallbackDepartmentId);

              handleDepartmentStatus(_primaryDepartment, _fallbackDepartment);
            }
          });
        });
      });
    }
  }, [props.departmentId, props.fallbackDepartmentId]);

  React.useEffect(function () {
    /**
     * Registers a callback to be fired when the number of unread messages changes.
     * [webWidget:on chat:unreadMessages]{@link https://developer.zendesk.com/embeddables/docs/widget/chat#on-chatunreadmessages}
     */
    zE('webWidget:on', 'chat:unreadMessages', function (count) {
      // Update redux with count for use wherever in host-app
      props.onUpdateUnreadMessagesCount(count);
    });

    /**
     * Executes a callback when the widget is opened.
     * [webWidget:on open]{@link https://developer.zendesk.com/embeddables/docs/widget/core#on-open}
     */
    zE('webWidget:on', 'open', function () {
      // We need to mark the messages as read ourselves and the callback above is not triggered when they're read.
      props.onUpdateUnreadMessagesCount(0);
      /**
       * Update redux that chat is open for manual chat-button click
       */
      props.onVisibilityChange(true);
    });
  }, [props.onUpdateUnreadMessagesCount, props.onVisibilityChange]);

  React.useEffect(function () {
    if (props.hidden || props.visibleAt) {
      /**
       * Executes a callback when the widget is closed.
       * [webWidget:on close]{@link https://developer.zendesk.com/embeddables/docs/widget/core#on-close}
       */
      zE('webWidget:on', 'close', function () {
        if (props.hidden || window.innerWidth < props.visibleAt) {
          /**
           * Hides all parts of the widget from the page.
           * [webWidget hide]{@link https://developer.zendesk.com/embeddables/docs/widget/core#hide}
           */
          zE('webWidget', 'hide');
        }
        // Update redux with state of widget (open/closed)
        props.onVisibilityChange(false);
      });
    }
  }, [props.visibleAt, props.hidden, props.onVisibilityChange]);

  /**
   * This hook sets the language of the widget
   * [webwidget setLocale]{@link https://developer.zendesk.com/embeddables/docs/widget/core#setlocale}
   */
  React.useEffect(function () {
    if (props.language) {
      zE(function () {
        zE('webWidget', 'setLocale', props.language);
      });
    }
  }, [props.language]);

  /**
   * These hooks use state and ref to compare the contents of current
   * and previous props.tags arrays and avoid updating zendesk on every render.
   */

  var _React$useState3 = React.useState(props.tags),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      tags = _React$useState4[0],
      setTags = _React$useState4[1];

  var prevTags = usePrev(props.tags);

  React.useEffect(function () {
    if (not(equals(prevTags, props.tags))) {
      setTags(props.tags);
    }
  }, [props.tags, prevTags]);

  /**
   * This hooks handles updating of the tags that the CS-agent will see
   */
  React.useEffect(function () {
    /**
     * If you log out tags should ideally be cleared in the host-app
     * But we want to keep the tags to faster help the customer
     * I.E. Only update if tags is not empty
     */
    if (not(isEmpty(tags))) {
      zE(function () {
        /**
         * [webWidget updateSettings]{@link https://developer.zendesk.com/embeddables/docs/widget/core#updatesettings}
         */
        zE('webWidget', 'updateSettings', {
          webWidget: { chat: { tags: tags } }
        });
      });
    }
  }, [tags]);

  /**
   * This hook handles updating name of player
   */
  React.useEffect(function () {
    if (props.name) {
      zE(function () {
        /**
         * Prefills the offline-form with customer-name if chat is offline
         * We set it as readOnly as we're appending internal username to name for faster handling
         * and we don't want them to be able to edit it.
         * [webwidget prefill]{@link https://developer.zendesk.com/embeddables/docs/widget/core#prefill}
         */
        zE('webWidget', 'prefill', {
          name: { value: props.name, readOnly: true }
        });
        /**
         * Identifies an end user to Zendesk
         * [webwidget identify]{@link https://developer.zendesk.com/embeddables/docs/widget/core#identify}
         */
        zE('webWidget', 'identify', { name: props.name });
      });
    }
  }, [props.name]);

  /**
   * This hook handles updating email of player
   * see name hook for more documentation
   */
  React.useEffect(function () {
    if (props.email) {
      zE(function () {
        zE('webWidget', 'prefill', {
          email: { value: props.email, readOnly: false }
        });
        zE('webWidget', 'identify', { email: props.email });
      });
    }
  }, [props.email]);

  /**
   * This hook handles the visibility of the launcher & widget
   * @prop props.disabled Override prop that always hides and disables the chat.
   * @prop props.showTab redux-state boolean that toggles if the chat should be open or collapsed
   * @prop visible local-state boolean that takes care of the launcher should be visible or not for current window.innerWidth
   */
  React.useEffect(function () {
    if (props.disabled || !props.showTab && !visible) {
      zE(function () {
        /**
         * Hides widget/launcher completely
         * [webWidget hide]{@link https://developer.zendesk.com/embeddables/docs/widget/core#hide}
         */
        zE('webWidget', 'hide');
      });
    } else if (props.showTab) {
      zE(function () {
        /**
         * If the widget was hidden we must first show it before we can un-collapse it
         * [webWidget show]{@link https://developer.zendesk.com/embeddables/docs/widget/core#show}
         */
        zE('webWidget', 'show');
        /**
         * Forces the widget to open/un-collapse.
         * [webWidget open]{@link https://developer.zendesk.com/embeddables/docs/widget/core#open}
         */
        zE('webWidget', 'open');
      });
    } else if (visible) {
      zE(function () {
        // If it was hidden, it must first be shown again, as above.
        zE('webWidget', 'show');
        /**
         * Forces the widget to close/collapse.
         * [webWidget close]{@link https://developer.zendesk.com/embeddables/docs/widget/core#close}
         */
        zE('webWidget', 'close');
      });
    }
  }, [props.disabled, props.showTab, visible]);

  return null;
}

function SupportChat(props) {
  var _React$useState5 = React.useState(false),
      _React$useState6 = _slicedToArray(_React$useState5, 2),
      initalized = _React$useState6[0],
      setInitalized = _React$useState6[1];

  /**
   * Window object with pre-defined chat-customizations that needs to be available
   * before or right after script has been loaded.
   * [zESettings]{@link https://developer.zendesk.com/embeddables/docs/widget/settings}
   */


  var initialzESettings = {
    webWidget: {
      chat: {
        concierge: {
          avatarPath: props.translate('chat.concierge.avatar'),
          name: props.translate('chat.concierge.name'),
          title: {
            '*': props.translate('chat.concierge.title')
          }
        },
        departments: {
          enabled: [props.departmentId, props.fallbackDepartmentId],
          select: props.departmentId
        },
        offlineForm: {
          greeting: {
            '*': props.translate('chat.offline-form.greeting')
          }
        },
        tags: props.tags,
        title: {
          '*': props.translate('chat.title')
        }
      },
      color: {
        launcher: props.color,
        theme: props.color
      },
      launcher: {
        chatLabel: {
          '*': props.translate('chat.launcher.chat-label')
        },
        label: {
          '*': props.translate('chat.launcher.label')
        }
      }
    }

    /**
     * This hook checks availability of zE api on window until it's available.
     * It then updates local-state to make <Chat/> available
     */
  };React.useEffect(function () {
    if (!initalized) {
      var timerId = window.setInterval(function () {
        setInitalized(typeof zE !== 'undefined' && typeof $zopim !== 'undefined');
      }, 250);
      return function () {
        clearInterval(timerId);
      };
    }
  }, [initalized]);

  return React.createElement(
    React.Fragment,
    null,
    React.createElement(
      Helmet,
      null,
      React.createElement(
        'script',
        { type: 'text/javascript' },
        'window.zESettings = ' + JSON.stringify(initialzESettings)
      ),
      props.addScriptInHead && React.createElement('script', {
        id: 'ze-snippet',
        src: 'https://static.zdassets.com/ekr/snippet.js?key=' + props.appKey,
        type: 'text/javascript'
      })
    ),
    !props.addScriptInHead && React.createElement('script', {
      id: 'ze-snippet',
      src: 'https://static.zdassets.com/ekr/snippet.js?key=' + props.appKey,
      type: 'text/javascript'
    }),
    initalized && React.createElement(Chat, props)
  );
}

SupportChat.defaultProps = {
  visibleAt: 800,
  addScriptInHead: false
};

SupportChat.propTypes = {
  addScriptInHead: PropTypes.bool,
  appKey: PropTypes.string.isRequired,
  color: PropTypes.string,
  departmentId: PropTypes.number.isRequired,
  disabled: PropTypes.bool,
  email: PropTypes.string,
  fallbackDepartmentId: PropTypes.number,
  hidden: PropTypes.bool,
  language: PropTypes.string,
  name: PropTypes.string,
  showTab: PropTypes.bool,
  tags: PropTypes.arrayOf(PropTypes.string),
  translate: PropTypes.func.isRequired,
  visibleAt: PropTypes.number,
  onUpdateUnreadMessagesCount: PropTypes.func.isRequired,
  onVisibilityChange: PropTypes.func.isRequired
};

var SupportChat$1 = compose(withTranslate, connect(function (state, props) {
  return {
    showTab: getTabVisibility(prop(props.mountPoint, state))
  };
}, function (dispatch) {
  return {
    onVisibilityChange: function onVisibilityChange(value) {
      return dispatch(setTabVisibility(value));
    },
    onUpdateUnreadMessagesCount: function onUpdateUnreadMessagesCount(count) {
      return dispatch(updateUnreadCount(count));
    }
  };
}))(SupportChat);

export { SupportChat$1 as SupportChat, updateUnreadCount, setTabVisibility, getUnreadCount, getTabVisibility, reducer };
